import { useMemo, useState } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { toUpper } from "lodash";

import { Button, Modal } from "@dpdgroupuk/mydpd-ui";

import {
  LIST_VIEW,
  MAP_VIEW,
  SELECT_PICKUP_LOCATION_TITLE,
} from "~/constants/strings";
import { PICKUP_POINT_TYPE } from "~/constants/pickupPoint";
import { getFilteredPickupPoints } from "~/models/pickupPoint";
import { Close, LeftArrow } from "~/components/Icon";
import PickupPointsMap from "../PickupPointsMap";
import Filters from "./Filters/Filters";
import ActivePickupPoint from "./ActivePickupPoint/ActivePickupPoint";
import PickupPointsList from "./PickupPointsList/PickupPointsList";
import styles from "./SelectPickupPointModal.module.scss";

const SelectPickupPointModal = ({
  deliveryAddress,
  open,
  onHide,
  pickupPoints,
  onPickupLocationChange,
  fetchPickupLocationsByPostcode,
}) => {
  const [searchPostcode, setSearchPostcode] = useState(null);
  const [postcodePickupLocations, setPostcodePickupLocations] = useState(null);
  const [activePickupPoint, setActivePickupPoint] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState([
    PICKUP_POINT_TYPE.SHOP,
    PICKUP_POINT_TYPE.LOCKER,
  ]);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [loadingPickupLocations, setLoadingPickupLocations] = useState(false);
  const [isListView, setIsListView] = useState(false);
  const isSearchPostcodeEqualAddressPostcode =
    toUpper(searchPostcode).replace(/ /g, "") ===
    toUpper(deliveryAddress.postcode).replace(/ /g, "");

  const filteredPickupPoints = useMemo(
    () =>
      getFilteredPickupPoints(
        postcodePickupLocations || pickupPoints,
        selectedTypes,
        selectedAmenities
      ),
    [pickupPoints, postcodePickupLocations, selectedAmenities, selectedTypes]
  );

  const onSearchPickupLocations = postcode => {
    setSearchPostcode(postcode);

    fetchPickupLocationsByPostcode({
      postcode,
      setPickupLocations: setPostcodePickupLocations,
      setIsLoading: setLoadingPickupLocations,
    });
  };

  return (
    <Modal show={open} centered size="sm" dialogClassName={styles.container}>
      <>
        <Modal.Header
          className={classNames(
            "w-100 align-items-center",
            activePickupPoint ? "p-2" : "p-3"
          )}
        >
          {activePickupPoint && (
            <div
              className={styles.headerIcon}
              onClick={() => setActivePickupPoint(null)}
            >
              <LeftArrow />
            </div>
          )}
          <span>{!activePickupPoint && SELECT_PICKUP_LOCATION_TITLE}</span>
          <div className={styles.headerIcon} onClick={onHide}>
            <Close />
          </div>
        </Modal.Header>

        {activePickupPoint && (
          <ActivePickupPoint
            pickupPoint={activePickupPoint}
            onSelectPickupPoint={() => {
              onHide();
              setActivePickupPoint(null);
              onPickupLocationChange(activePickupPoint);
            }}
          />
        )}
        <Modal.Body
          className={classNames(styles.body, activePickupPoint && "d-none")}
        >
          <div className={styles.mapContainer}>
            <div className={styles.mapShadow} />
            <PickupPointsMap
              deliveryAddress={deliveryAddress}
              pickupPoints={filteredPickupPoints}
              onPickupPointClick={setActivePickupPoint}
              isSearchByDifferentPostcode={
                searchPostcode && !isSearchPostcodeEqualAddressPostcode
              }
            />
          </div>
          <div
            className={classNames(
              styles.listContainer,
              isListView ? styles.expandedList : styles.collapsedList
            )}
          >
            <Button
              variant="light"
              onClick={() => setIsListView(!isListView)}
              className={classNames(
                "ml-3 mb-3 rounded-pill font-weight-bold bg-white border-0",
                styles.expandListButton
              )}
            >
              {toUpper(isListView ? MAP_VIEW : LIST_VIEW)}
            </Button>
            <Filters
              selectedTypes={selectedTypes}
              setSelectedTypes={setSelectedTypes}
              selectedAmenities={selectedAmenities}
              setSelectedAmenities={setSelectedAmenities}
              deliveryAddress={deliveryAddress}
              onSearchPickupLocations={onSearchPickupLocations}
            />
            <div
              className={classNames(
                styles.pickupPointsListContainer,
                "bg-white rounded-bottom"
              )}
            >
              <PickupPointsList
                pickupPoints={filteredPickupPoints}
                setActivePickupPoint={setActivePickupPoint}
                isLoading={loadingPickupLocations}
              />
            </div>
          </div>
        </Modal.Body>
      </>
    </Modal>
  );
};

SelectPickupPointModal.propTypes = {
  deliveryAddress: PropTypes.object,
  open: PropTypes.bool,
  onHide: PropTypes.func,
  pickupPoints: PropTypes.array,
  selectedPickupPoint: PropTypes.object,
  onPickupLocationChange: PropTypes.func,
  fetchPickupLocationsByPostcode: PropTypes.func,
};

export default SelectPickupPointModal;
